import type {AutoColor, IconName} from '@wandb/components';

/**
 * AgentSuggestion is one new-chat suggestion chip. `prompt` is the text sent on
 * click and defaults to `title`. `sentLabel` is what the sent bubble reads once
 * clicked, for chips whose title works as a button but not as something the
 * user would say; it defaults to `title`.
 */
export interface AgentSuggestion {
  title: string;
  prompt?: string;
  sentLabel?: string;
  icon: IconName;
  color: AutoColor;
}

/**
 * Prompts submitted by ARIA entry points outside the chat drawer.
 */
export const entryPointPrompts = {
  filterRuns: (request: string) =>
    `Update this workspace's run filters based on this request: ${request}`,
} as const;

/** Contextual composer guidance shown by non-submitting ARIA entry points. */
export const entryPointPlaceholders = {
  runsColumns: "Describe the columns you'd like to show",
  createReport: "Describe the report you'd like to create",
  improveReport: "Describe how you'd like to improve this report",
  createSavedView: "Describe the saved view you'd like to create",
  run: 'Ask ARIA about this run',
  panel: 'Ask ARIA about this panel',
} as const;

export type WBAgentCallKind = 'trace' | 'evaluation' | 'evaluation-result';

export const callReferenceLabels: Record<WBAgentCallKind, string> = {
  trace: 'Trace',
  evaluation: 'Eval',
  'evaluation-result': 'Eval result',
};

export function callReferenceKind(displayName?: string): WBAgentCallKind {
  const label = displayName?.toLowerCase();
  if (label?.startsWith('eval result')) {
    return 'evaluation-result';
  }
  if (label?.startsWith('eval')) {
    return 'evaluation';
  }
  return 'trace';
}

export function callReferencePlaceholder(
  kind: WBAgentCallKind,
  count = 1
): string {
  const noun = callReferenceLabels[kind].toLowerCase();
  return `Ask ARIA about ${count === 1 ? `this ${noun}` : `these ${noun}s`}`;
}

// Suggestion chips use one consistent accent across routes.

// Shared

export const summarizeReport: AgentSuggestion = {
  title: 'Summarize this project in a report',
  icon: 'report',
  color: 'teal',
};

export const inferenceModels: AgentSuggestion = {
  title: 'What models can I use on Inference?',
  icon: 'magic-wand-star',
  color: 'teal',
};

export const draftAReport: AgentSuggestion = {
  title: 'Help me draft a report',
  icon: 'report',
  color: 'teal',
};

export const summarizeRecentWork: AgentSuggestion = {
  title: 'Summarize my recent work',
  icon: 'recent-clock',
  color: 'teal',
};

export const patterns: AgentSuggestion = {
  title: 'What patterns do you see in my results?',
  icon: 'chart-line',
  color: 'teal',
};

export const whatCanYouDo: AgentSuggestion = {
  title: 'What can you do?',
  prompt: '/intro',
  icon: 'help-alt',
  color: 'teal',
};

export const roastMyProject: AgentSuggestion = {
  title: 'Roast my project',
  icon: 'explosion',
  color: 'teal',
};

export const recommendNextExperiment: AgentSuggestion = {
  title: 'Recommend my next experiment',
  icon: 'lightbulb-info',
  color: 'teal',
};

// ARIA landing — onboarding CTAs. Each sends a slash command that launches the
// matching onboarding skill under the hood.

export const onboardingFineTuning: AgentSuggestion = {
  title: 'Get started with post-training',
  prompt: '/onboarding-fine-tuning',
  sentLabel: 'Can you help me get started with post-training?',
  icon: 'settings-parameters',
  color: 'teal',
};

export const onboardingAgentMonitoring: AgentSuggestion = {
  title: 'Get started with agent monitoring',
  prompt: '/onboarding-agent-monitoring',
  sentLabel: 'Can you help me get started with agent monitoring?',
  icon: 'automation-robot-arm',
  color: 'teal',
};

export const onboardingModelTraining: AgentSuggestion = {
  title: 'Get started with model training',
  prompt: '/onboarding-model-training',
  sentLabel: 'Can you help me get started with model training?',
  icon: 'chart-line',
  color: 'teal',
};

// Workspace

export const autoresearchQuickstart: AgentSuggestion = {
  title: 'Launch experiments with autoresearch',
  prompt: '/autoresearch-quickstart',
  icon: 'rocket-launch',
  color: 'teal',
};

// Default

export const recentCrashedRuns: AgentSuggestion = {
  title: 'Have any of my recent runs crashed?',
  icon: 'warning',
  color: 'teal',
};

export const starredReports: AgentSuggestion = {
  title: 'Find the reports I starred',
  icon: 'star',
  color: 'teal',
};

export const recentlyFinishedRuns: AgentSuggestion = {
  title: 'Which runs finished most recently?',
  icon: 'recent-clock',
  color: 'teal',
};

// Runs table

export const setBaseline: AgentSuggestion = {
  title: 'Set my best run as the baseline',
  icon: 'baseline-alt',
  color: 'teal',
};

// Run logs

export const analyzeLogs: AgentSuggestion = {
  title: 'Analyze my logs',
  icon: 'list-bullets',
  color: 'teal',
};

// Run (Overview)

export const compareRecentRuns: AgentSuggestion = {
  title: 'How does this run compare to recent runs?',
  icon: 'chart-scatterplot',
  color: 'teal',
};

// Non-project / org / profile

export const crashedRuns: AgentSuggestion = {
  title: 'How many of my runs crashed this past week?',
  icon: 'warning',
  color: 'teal',
};

export const activeRuns: AgentSuggestion = {
  title: 'Find all of my active runs',
  icon: 'running-repeat',
  color: 'teal',
};

export const starProjects: AgentSuggestion = {
  title: 'Star the projects I visit the most',
  icon: 'star',
  color: 'teal',
};

// Registry

export const collectionsActivity: AgentSuggestion = {
  title: 'Show me collections with recent activity',
  icon: 'recent-clock',
  color: 'teal',
};

export const cleanupRegistries: AgentSuggestion = {
  title: 'Help me clean up my registries',
  icon: 'delete',
  color: 'teal',
};

export const registriesWithAutomations: AgentSuggestion = {
  title: 'Which Registries have Automations?',
  icon: 'automation-robot-arm',
  color: 'teal',
};

// Automations

export const understandAutomations: AgentSuggestion = {
  title: 'Help me understand these automations',
  icon: 'automation-robot-arm',
  color: 'teal',
};

export const createAutomation: AgentSuggestion = {
  title: 'Help me create an automation',
  icon: 'add-new',
  color: 'teal',
};

export const configureGithubAction: AgentSuggestion = {
  title: 'Help me configure a Github action',
  icon: 'github',
  color: 'teal',
};

// Weave (conversations)

export const frustratedUsers: AgentSuggestion = {
  title: 'What did frustrated users say?',
  icon: 'thumbs-down',
  color: 'teal',
};

export const summarizeConversations: AgentSuggestion = {
  title: 'Summarize the last conversation/traces',
  icon: 'forum-chat-bubble',
  color: 'teal',
};

// Weave (signals)

export const createSignal: AgentSuggestion = {
  title: 'Create a new signal',
  icon: 'add-new',
  color: 'teal',
};

export const signalsFound: AgentSuggestion = {
  title: 'What signals have you found?',
  icon: 'search',
  color: 'teal',
};

// Weave (evaluations)

export const createDataset: AgentSuggestion = {
  title: 'Help me create a dataset',
  icon: 'table',
  color: 'teal',
};

export const createScorers: AgentSuggestion = {
  title: 'Help me create scorers',
  icon: 'number-hashtag',
  color: 'teal',
};

export const evalImproved: AgentSuggestion = {
  title: 'Did my latest eval improve and why?',
  icon: 'chart-line',
  color: 'teal',
};

// Sweeps list

export const bestSweep: AgentSuggestion = {
  title: 'Which sweep found the best result?',
  icon: 'magic-wand-star',
  color: 'teal',
};

export const runningSweeps: AgentSuggestion = {
  title: 'Which sweeps are still running?',
  icon: 'running-repeat',
  color: 'teal',
};

export const sweepParameters: AgentSuggestion = {
  title: 'Which parameters mattered most?',
  icon: 'settings-parameters',
  color: 'teal',
};

// Project jobs

export const describeJobs: AgentSuggestion = {
  title: 'What do these jobs do?',
  icon: 'info',
  color: 'teal',
};

export const recentJobRuns: AgentSuggestion = {
  title: 'Which jobs ran most recently?',
  icon: 'recent-clock',
  color: 'teal',
};

// Launch (queues, agents)

export const stuckQueues: AgentSuggestion = {
  title: 'Are any of my queues stuck?',
  icon: 'warning',
  color: 'teal',
};

export const queueActivity: AgentSuggestion = {
  title: 'Summarize activity across my queues',
  icon: 'list-bullets',
  color: 'teal',
};

// Project overview — Details

export const projectSizeAndActivity: AgentSuggestion = {
  title: "Summarize this project's size and activity",
  icon: 'chart-line',
  color: 'teal',
};

export const projectVisibility: AgentSuggestion = {
  title: 'Who can see this project?',
  icon: 'info',
  color: 'teal',
};

export const projectApiSnippet: AgentSuggestion = {
  title: 'Show me the API code to pull these runs',
  icon: 'list-bullets',
  color: 'teal',
};

// Project overview — Weave tab

export const traceVolumeAndCost: AgentSuggestion = {
  title: 'Summarize my trace volume and cost',
  icon: 'chart-line',
  color: 'teal',
};

export const tokenSpend: AgentSuggestion = {
  title: 'What is driving my token spend?',
  icon: 'number-hashtag',
  color: 'teal',
};

export const traceErrors: AgentSuggestion = {
  title: 'Are traces erroring more than usual?',
  icon: 'warning',
  color: 'teal',
};

// Project overview — Project Roles tab

export const projectAccess: AgentSuggestion = {
  title: 'Who has access to this project?',
  icon: 'info',
  color: 'teal',
};

export const projectAdmins: AgentSuggestion = {
  title: 'Which members are admins?',
  icon: 'search',
  color: 'teal',
};

export const explainRoles: AgentSuggestion = {
  title: 'Explain these project roles',
  icon: 'list-bullets',
  color: 'teal',
};

// Project overview — AI Providers tab

export const configuredProviders: AgentSuggestion = {
  title: 'Which AI providers are configured?',
  icon: 'info',
  color: 'teal',
};

export const addCustomRuntime: AgentSuggestion = {
  title: 'Help me add a custom runtime',
  icon: 'add-new',
  color: 'teal',
};

export const whereProvidersApply: AgentSuggestion = {
  title: 'Where do these providers get used?',
  icon: 'search',
  color: 'teal',
};

// Serverless Inference catalog

export const modelFeatureSupport: AgentSuggestion = {
  title: 'Which of these support tool calling?',
  icon: 'magic-wand-star',
  color: 'teal',
};

export const compareModelPricing: AgentSuggestion = {
  title: 'Compare these models on price and context',
  icon: 'chart-scatterplot',
  color: 'teal',
};

export const callThisModel: AgentSuggestion = {
  title: 'Show me how to call one of these models',
  icon: 'list-bullets',
  color: 'teal',
};

// Marimo notebooks

export const startANotebook: AgentSuggestion = {
  title: 'Help me start a notebook',
  icon: 'add-new',
  color: 'teal',
};

export const whatNotebooksDo: AgentSuggestion = {
  title: 'What are these notebooks doing?',
  icon: 'info',
  color: 'teal',
};

export const cleanUpNotebooks: AgentSuggestion = {
  title: 'Help me clean up old notebooks',
  icon: 'delete',
  color: 'teal',
};

// Reports list

export const recentReports: AgentSuggestion = {
  title: 'Which reports changed recently?',
  icon: 'recent-clock',
  color: 'teal',
};

export const whatReportsCover: AgentSuggestion = {
  title: 'What do these reports cover?',
  icon: 'info',
  color: 'teal',
};

// A single report

export const summarizeThisReport: AgentSuggestion = {
  title: 'Summarize this report',
  icon: 'report',
  color: 'teal',
};

export const improveThisReport: AgentSuggestion = {
  title: 'Improve this report',
  icon: 'magic-wand-star',
  color: 'teal',
};

export const checkReportNumbers: AgentSuggestion = {
  title: 'Check these numbers against the runs',
  icon: 'search',
  color: 'teal',
};

// Artifacts

export const whatArtifactsExist: AgentSuggestion = {
  title: 'What artifacts does this project hold?',
  icon: 'table',
  color: 'teal',
};

export const cleanUpArtifacts: AgentSuggestion = {
  title: 'Help me clean up old versions',
  icon: 'delete',
  color: 'teal',
};

export const artifactLineage: AgentSuggestion = {
  title: 'Where did this artifact come from?',
  icon: 'search',
  color: 'teal',
};

// Weave Playground

export const comparePromptModels: AgentSuggestion = {
  title: 'Compare two models on this prompt',
  icon: 'chart-scatterplot',
  color: 'teal',
};

export const improveThisPrompt: AgentSuggestion = {
  title: 'Help me write a better prompt',
  icon: 'magic-wand-star',
  color: 'teal',
};

export const promptToEval: AgentSuggestion = {
  title: 'Turn this into an evaluation',
  icon: 'number-hashtag',
  color: 'teal',
};

// A single sweep

export const bestRunsInSweep: AgentSuggestion = {
  title: 'Which runs in this sweep did best?',
  icon: 'magic-wand-star',
  color: 'teal',
};

export const keepSweepRunning: AgentSuggestion = {
  title: 'Should I keep this sweep running?',
  icon: 'lightbulb-info',
  color: 'teal',
};

// Profile — Stars tab

export const whatIStarred: AgentSuggestion = {
  title: 'Remind me what I starred',
  icon: 'search',
  color: 'teal',
};

export const activeStarredProjects: AgentSuggestion = {
  title: 'Which of these are still active?',
  icon: 'running-repeat',
  color: 'teal',
};

// Weave threads

export const slowestThreads: AgentSuggestion = {
  title: 'Which threads are slowest?',
  icon: 'chart-line',
  color: 'teal',
};

export const longestThreads: AgentSuggestion = {
  title: 'Which threads took the most turns?',
  icon: 'number-hashtag',
  color: 'teal',
};

export const threadTopics: AgentSuggestion = {
  title: 'What are people asking about?',
  icon: 'forum-chat-bubble',
  color: 'teal',
};

// Weave leaderboards

export const leaderboardWinner: AgentSuggestion = {
  title: 'Which model is ahead, and on what?',
  icon: 'magic-wand-star',
  color: 'teal',
};

export const leaderboardGaps: AgentSuggestion = {
  title: 'Which models are missing results?',
  icon: 'search',
  color: 'teal',
};

export const buildLeaderboard: AgentSuggestion = {
  title: 'Help me build a leaderboard',
  icon: 'add-new',
  color: 'teal',
};

// Weave assets — shared across the class lists

export const assetInventory: AgentSuggestion = {
  title: 'What is published here?',
  icon: 'table',
  color: 'teal',
};

export const assetRecentChanges: AgentSuggestion = {
  title: 'What changed most recently?',
  icon: 'recent-clock',
  color: 'teal',
};

export const assetUsage: AgentSuggestion = {
  title: 'Which of these are actually used?',
  icon: 'search',
  color: 'teal',
};

// Weave assets — per class

export const modelVersionHistory: AgentSuggestion = {
  title: 'How has this model changed?',
  icon: 'chart-line',
  color: 'teal',
};

export const datasetContents: AgentSuggestion = {
  title: 'What is in these datasets?',
  icon: 'table',
  color: 'teal',
};

export const promptVariables: AgentSuggestion = {
  title: 'What variables do these prompts take?',
  icon: 'number-hashtag',
  color: 'teal',
};

export const scorerBehavior: AgentSuggestion = {
  title: 'What do these scorers measure?',
  icon: 'number-hashtag',
  color: 'teal',
};

export const evalDefinitionContents: AgentSuggestion = {
  title: 'What does each evaluation run?',
  icon: 'info',
  color: 'teal',
};

export const opInventory: AgentSuggestion = {
  title: 'What does this project instrument?',
  icon: 'table',
  color: 'teal',
};

// One Launch queue

export const queueNotRunning: AgentSuggestion = {
  title: "Why isn't this queue running?",
  icon: 'warning',
  color: 'teal',
};

export const queueFailures: AgentSuggestion = {
  title: 'Which runs from this queue failed?',
  icon: 'search',
  color: 'teal',
};
